import { getCustomReceivablePaymentLabel } from './customReceivablePaymentMethods';

export type ReceivablePaymentMethodOption = { value: string; label: string; isCustom?: boolean };

/** Formas de pagamento/recebimento (contas a receber, caixa, orçamentos). */
export const RECEIVABLE_PAYMENT_METHODS: ReceivablePaymentMethodOption[] = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'credito_link_pagamento', label: 'Crédito - link de pagamento' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'convenio', label: 'Convênio / Plano' },
  { value: 'cheque', label: 'Cheque' },
];

export const BUDGET_PAYMENT_METHOD_LABELS = [
  'Dinheiro',
  'Cartão de Crédito',
  'Cartão de Débito',
  'Crédito - link de pagamento',
  'PIX',
  'Boleto',
  'Transferência',
  'Convênio',
] as const;

export function getReceivablePaymentMethodLabel(value: string | null | undefined): string {
  if (!value) return '-';
  const custom = getCustomReceivablePaymentLabel(value);
  if (custom) return custom;
  return RECEIVABLE_PAYMENT_METHODS.find((m) => m.value === value)?.label || value;
}

const PAYMENT_METHOD_SLUG_ALIASES: Record<string, string> = {
  cash: 'dinheiro',
  credit_card: 'cartao_credito',
  debit_card: 'cartao_debito',
  credit_payment_link: 'credito_link_pagamento',
  bank_transfer: 'transferencia',
  check: 'cheque',
  insurance: 'convenio',
  other: 'outros',
};

/** Normaliza slug/alias de forma de pagamento para lookup de label. */
export function normalizeReceivablePaymentMethodSlug(value?: string | null): string {
  if (!value) return 'outros';
  const raw = String(value).trim();
  const v = raw.toLowerCase().replace(/\s+/g, '_');
  if (PAYMENT_METHOD_SLUG_ALIASES[v]) return PAYMENT_METHOD_SLUG_ALIASES[v];
  const ascii = v.normalize('NFD').replace(/\p{M}/gu, '');
  if (PAYMENT_METHOD_SLUG_ALIASES[ascii]) return PAYMENT_METHOD_SLUG_ALIASES[ascii];
  if (/cart[aã]o\s*de\s*cr[eé]dito/i.test(raw)) return 'cartao_credito';
  if (/cart[aã]o\s*de\s*d[eé]bito/i.test(raw)) return 'cartao_debito';
  if (/cr[eé]dito\s*[-–]\s*link/i.test(raw)) return 'credito_link_pagamento';
  return ascii || v;
}

/** Labels em inglês (recibos / integrações legadas). */
export const RECEIPT_PAYMENT_LABELS: Record<string, string> = {
  cash: 'Dinheiro',
  credit_card: 'Cartão Crédito',
  debit_card: 'Cartão Débito',
  credit_payment_link: 'Crédito - link de pagamento',
  credito_link_pagamento: 'Crédito - link de pagamento',
  pix: 'PIX',
  bank_transfer: 'Transferência',
  check: 'Cheque',
  insurance: 'Convênio',
  other: 'Outro',
};

export function getPaymentMethodDisplayLabel(value?: string | null): string {
  if (!value) return '—';
  const custom = getCustomReceivablePaymentLabel(value);
  if (custom) return custom;
  const slug = normalizeReceivablePaymentMethodSlug(value);
  const fromList = RECEIVABLE_PAYMENT_METHODS.find((m) => m.value === slug)?.label;
  if (fromList) return fromList;
  const key = String(value).trim().toLowerCase();
  if (RECEIPT_PAYMENT_LABELS[key]) return RECEIPT_PAYMENT_LABELS[key];
  if (slug === 'outros') return 'Outros';
  return String(value).trim() || '—';
}

/** Limite alinhado às maquininhas usadas pela clínica. */
export const MAX_CARD_INSTALLMENTS = 12;

export const CARD_INSTALLMENT_OPTIONS: number[] = Array.from(
  { length: MAX_CARD_INSTALLMENTS },
  (_, i) => i + 1,
);

/**
 * Métodos em que o paciente pode parcelar. Aceita os slugs em pt/en e os rótulos
 * livres usados nos orçamentos ("Cartão de Crédito").
 */
export function isDebitCardPaymentMethod(value: string | null | undefined): boolean {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return false;
  if (v === 'cartao_debito' || v === 'debit_card') return true;
  return v.includes('debito') || v.includes('débito');
}

export function isCardInstallmentPaymentMethod(value: string | null | undefined): boolean {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return false;
  if (isDebitCardPaymentMethod(v)) return false;
  if (v === 'cartao_credito' || v === 'credit_card') return true;
  if (v === 'credito_link_pagamento' || v === 'credit_payment_link') return true;
  return v.includes('credito') || v.includes('crédito');
}

export function isAnyCardPaymentMethod(value: string | null | undefined): boolean {
  return isCardInstallmentPaymentMethod(value) || isDebitCardPaymentMethod(value);
}

/**
 * Nº de parcelas gravado na transação. Tolera o formato legado do contas a
 * receber, onde `installments` pode chegar como a lista de parcelas.
 */
export function parseCardInstallments(raw: unknown): number {
  const value = Array.isArray(raw) ? raw.length : Number(raw);
  if (!Number.isFinite(value) || Number(value) < 1) return 1;
  return Math.min(Math.floor(Number(value)), MAX_CARD_INSTALLMENTS);
}

/** Ex.: "6x" ou "1x (à vista)" — usado nas opções de parcelamento e nos resumos. */
export function formatCardInstallmentOption(installments: number, _total?: number): string {
  if (installments <= 1) return '1x (à vista)';
  return `${installments}x`;
}

/** Ex.: "Cartão de Crédito · 3x" — parcelas no método, não na descrição. */
export function formatPaymentMethodWithInstallments(
  method?: string | null,
  installments?: unknown,
): string {
  const label = getPaymentMethodDisplayLabel(method);
  if (!isCardInstallmentPaymentMethod(method)) return label;
  return `${label} · ${parseCardInstallments(installments)}x`;
}
